import classNames from "@casino/external/classNames";
import { type CSSProperties, forwardRef } from "react";

type StackProps = React.ComponentProps<"div"> & {
  justifyContent?: string;
  justifyItems?: string;
  justifySelf?: string;
  alignItems?: string;
  alignContent?: string;
  alignSelf?: string;
  placeItems?: string;
  placeSelf?: string;
  templateColumns?: string;
  templateRows?: string;
  templateAreas?: string;
  borderRadius?: string;
  padding?: string;
  relative?: boolean;
  absolute?: boolean;
  autoFlow?: string;
  overflow?: string;
  margin?: string;
  marginTop?: string;
  marginBottom?: string;
  zIndex?: number;
  autoColumns?: string;
  autoRows?: "min-content" | "max-content" | "auto";
  direction?: "ltr" | "rtl";
  maxWidth?: string;
  maxHeight?: string;
  width?: string;
  height?: string;
  gridArea?: string;
  center?: boolean;
  minWidth?: string;
  minHeight?: string;
  gridGap?: string;
  gridColumn?: string;
  gridRow?: string;
  space?: string | number;
};

const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      className,
      style,
      children,
      justifyContent,
      justifyItems,
      justifySelf,
      alignItems,
      alignContent,
      alignSelf,
      placeItems,
      placeSelf,
      templateColumns,
      templateRows,
      templateAreas,
      borderRadius,
      padding,
      relative,
      absolute,
      autoFlow,
      overflow,
      margin,
      marginTop,
      marginBottom,
      zIndex,
      autoColumns,
      autoRows,
      direction,
      maxWidth,
      maxHeight,
      width,
      height,
      gridArea,
      center,
      minWidth,
      minHeight,
      gridGap,
      gridColumn,
      gridRow,
      space: _space,
      ...rest
    },
    ref,
  ) => {
    const dynamicStyle = Object.fromEntries(
      Object.entries({
        justifyContent: justifyContent || undefined,
        justifyItems: justifyItems || undefined,
        justifySelf: justifySelf || undefined,
        alignItems: alignItems || undefined,
        alignContent: alignContent || undefined,
        alignSelf: alignSelf || undefined,
        placeItems: placeItems || undefined,
        placeSelf: placeSelf || undefined,
        gridTemplateColumns: templateColumns || undefined,
        gridTemplateRows: templateRows || undefined,
        gridTemplateAreas: templateAreas ? `"${templateAreas}"` : undefined,
        borderRadius: borderRadius || undefined,
        padding: padding || undefined,
        gridAutoFlow: autoFlow || undefined,
        overflow: overflow || undefined,
        margin: margin || undefined,
        marginTop: marginTop || undefined,
        marginBottom: marginBottom || undefined,
        zIndex: zIndex ?? undefined,
        width: width || undefined,
        height: height || undefined,
        gridArea: gridArea || undefined,
        gridAutoColumns: autoColumns || undefined,
        gridAutoRows: autoRows || undefined,
        gridColumn: gridColumn || undefined,
        gridRow: gridRow || undefined,
        maxWidth: maxWidth || undefined,
        maxHeight: maxHeight || undefined,
        minWidth: minWidth || undefined,
        minHeight: minHeight || undefined,
        gap: gridGap || undefined,
        direction: direction || undefined,
        ...style,
      }).filter(([, value]) => value !== undefined),
    ) as CSSProperties;

    return (
      <div
        ref={ref}
        className={classNames(
          "grid gap-2",
          relative && "relative",
          absolute && "absolute",
          center && "place-items-center",
          className,
        )}
        style={dynamicStyle}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

Stack.displayName = "Stack";

export type { StackProps };
export default Stack;
